import React, { useState } from "react";
import { Share2, Check } from "lucide-react";
import { toast } from "sonner";
import { PUBLIC_BASE } from "@/hooks/useSEO";

/**
 * Share the current page (or a specific path) via the native Web-Share API
 * when available — falls back to clipboard copy on desktop browsers.
 *
 * Props:
 *   - title       Title shown in the share sheet (defaults to document.title)
 *   - text        Short description / message (defaults to og:description)
 *   - path        Optional sub-path appended to https://popoyo.app (defaults to current location.pathname)
 *   - variant     "solid" (coral pill) | "ghost" (text-link)
 *   - label       Optional custom label; defaults to "Share"
 *   - className   Extra classes
 */
export default function ShareButton({
  title,
  text,
  path,
  variant = "solid",
  label = "Share",
  className = "",
  testId = "share-page-btn",
}) {
  const [justCopied, setJustCopied] = useState(false);

  const handleShare = async () => {
    const shareUrl = path
      ? `${PUBLIC_BASE}${path.startsWith("/") ? path : `/${path}`}`
      : (typeof window !== "undefined" ? `${PUBLIC_BASE}${window.location.pathname}${window.location.search}` : PUBLIC_BASE);
    const shareTitle = title || (typeof document !== "undefined" ? document.title : "Popoyo.app");
    const shareText = text || (typeof document !== "undefined"
      ? (document.querySelector('meta[name="description"]')?.getAttribute("content") || "")
      : "");

    // Mobile / supported browsers — open native share sheet
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
        return;
      } catch (e) {
        // User cancelled — bail silently
        if (e?.name === "AbortError") return;
        // Otherwise fall through to clipboard
      }
    }

    // Desktop fallback — clipboard
    try {
      await navigator.clipboard.writeText(shareUrl);
      setJustCopied(true);
      toast.success(`Link copied: ${shareUrl}`);
      setTimeout(() => setJustCopied(false), 2200);
    } catch (_e) {
      // Final fallback — prompt the user manually
      window.prompt("Copy this link:", shareUrl);
    }
  };

  const base =
    "inline-flex items-center gap-1.5 rounded-full font-semibold transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[color:var(--coral)]/50";
  const styles =
    variant === "ghost"
      ? "px-3 py-1.5 text-sm text-[color:var(--ink-2)] hover:text-[color:var(--coral)]"
      : "px-4 py-2 text-sm text-white bg-[color:var(--coral)] hover:bg-[color:var(--coral)]/90 shadow-sm";

  return (
    <button
      type="button"
      onClick={handleShare}
      data-testid={testId}
      className={`${base} ${styles} ${className}`}
      aria-label="Share this page"
    >
      {justCopied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
      <span>{justCopied ? "Copied!" : label}</span>
    </button>
  );
}
